import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useOutletContext, useLocation } from 'react-router-dom';
import { NavigationBar } from '../components/ui/NavigationBar';
import { PageTransition } from '../components/ui/PageTransition';
import { useTranslations } from '../i18n';
import { useCarTasks } from '../hooks/useCarTasks';
import { useLogEntries } from '../hooks/useCarData';
import { useAuth } from '../contexts/AuthContext';
import { estimateMileageAtDate } from '../lib/mileageUtils';
import { generateMaintenanceSchedule } from '../lib/aiService';
import { AlertModal } from '../components/ui/AlertModal';
import { supabase, CarTask } from '../lib/supabase';
import { Wrench, Eye, Droplet, Plus, Brain, Sparkles, Activity, AlertCircle, Loader2, Trash2, AlertTriangle } from 'lucide-react';
import { theme, styles } from '../lib/theme';
import { CarTaskForm } from './CarTaskForm';

export function CarTasksView() {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslations();
    const { isAdmin } = useAuth();
    
    const isAdminPath = location.pathname.includes('/admin/');
    
    const outletContext = useOutletContext<any>() || {};
    const car = outletContext.selectedCar || outletContext.car;
    const preferences = outletContext.preferences;
    
    const { tasks, isLoading: tasksLoading, isFetching: tasksFetching, createTask, updateTask, deleteTask } = useCarTasks(car?.id);
    const logEntries = outletContext.logEntries || [];
    const logEntriesLoading = outletContext.logEntriesLoading;
    
    // No artificial delays, just wait for the tasks list itself
    const isLoading = tasksLoading && tasks.length === 0;

    const [isGenerating, setIsGenerating] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [sortMode, setSortMode] = useState<'date' | 'type' | 'priority'>('priority');
    const [alertConfig, setAlertConfig] = useState<{ 
        isOpen: boolean; 
        title: string; 
        message: string;
        confirmLabel?: string;
        secondaryAction?: { label: string; onClick: () => void };
    }>({ isOpen: false, title: '', message: '' });
    
    const estimatedMileage = outletContext.estimatedMileage;
    
    const isPro = preferences?.plan_type ? preferences.plan_type !== 'free' : false;
    const canGenerate = isAdminPath || (isPro && !car?.tasks_generated_at);

    const handleGenerate = async () => {
        if (!car) return;
        setIsGenerating(true);
        try {
            const newTasks = await generateMaintenanceSchedule(car, preferences?.language || 'en', tasks || []);
            
            if (newTasks && newTasks.length > 0) {
                for (const t of newTasks) {
                    await createTask.mutateAsync(t as any);
                }
                await supabase.from('cars').update({ tasks_generated_at: new Date().toISOString() }).eq('id', car.id);
                setAlertConfig({ isOpen: true, title: t.common?.ok || 'Success', message: 'Tasks generated!' });
            } else {
                setAlertConfig({ isOpen: true, title: t.common?.error || 'Error', message: 'Could not generate tasks.' });
            }
        } catch (e: any) {
            setAlertConfig({ isOpen: true, title: t.common?.error || 'Error', message: e.message || 'Error' });
        } finally {
            setIsGenerating(false);
        }
    };
    const handleDeleteAll = async () => {
        if (!isAdminPath || !car || !tasks || tasks.length === 0) return;
        
        setAlertConfig({
            isOpen: true,
            title: t.carTasks.deleteAllTitle,
            message: t.carTasks.deleteAllMessage.replace('{count}', String(tasks.length)),
            confirmLabel: t.common.cancel,
            secondaryAction: {
                label: t.common.delete,
                onClick: performDeleteAll
            }
        });
    };

    const performDeleteAll = async () => {
        if (!car) return;
        setIsSubmitting(true);
        try {
            const { error } = await supabase.from('car_tasks').delete().eq('car_id', car.id);
            if (error) throw error;
            
            await updateTask.client.invalidateQueries({ queryKey: ['car_tasks', car.id] });
            
            setAlertConfig({ 
                isOpen: true, 
                title: t.common.done, 
                message: t.carTasks.deleteAllSuccess,
                confirmLabel: t.common.ok
            });
        } catch (e: any) {
            setAlertConfig({ 
                isOpen: true, 
                title: 'Kļūda', 
                message: e.message || 'Neizdevās izdzēst uzdevumus.',
                confirmLabel: 'Labi'
            });
        } finally {
            setIsSubmitting(false);
        }
    };
    
    const getTaskStatus = (task: CarTask) => {
        let isOverdue = false;
        let remainingDistance = null;
        let remainingDays = null;
        
        // Use last performed mileage, or fall back to purchase mileage
        // Use last performed mileage, or fall back to estimated mileage at that date, or purchase mileage
        let baselineMileage = task.last_performed_mileage;
        
        if (baselineMileage === null && task.last_performed_date) {
            // If we have a date but no mileage record, estimate what the mileage was then
            baselineMileage = estimateMileageAtDate(
                car?.purchase_mileage,
                car?.bought_at,
                logEntries || [],
                car?.estimated_yearly_km || 15000,
                new Date(task.last_performed_date)
            );
        }
        
        if (baselineMileage === null) {
            baselineMileage = Number(car?.purchase_mileage ?? 0);
        } else {
            baselineMileage = Number(baselineMileage);
        }
        
        // Use last performed date, or fall back to car purchase date or creation date
        const baselineDate = task.last_performed_date ? new Date(task.last_performed_date) : (car?.bought_at ? new Date(car.bought_at) : new Date(task.created_at));
        
        // A task is "New/Initial" only if it has never been performed
        const wasNeverPerformed = task.last_performed_mileage === null && task.last_performed_date === null;
        
        const targetDistance = wasNeverPerformed ? Number(task.first_interval_distance || task.interval_distance || 0) : Number(task.interval_distance || 0);
        const targetMonths = wasNeverPerformed ? Number(task.first_interval_months || task.interval_months || 0) : Number(task.interval_months || 0);
        
        const isDataLoading = logEntriesLoading || outletContext.logEntriesFetching || tasksFetching;
        
        if (estimatedMileage === null || isDataLoading) {
            return { isOverdue: false, progress: 0, remainingDistance: null, remainingDays: null, statusType: 'ok' };
        }

        const currentMileage = Number(estimatedMileage);
        const drivenSinceLast = currentMileage - baselineMileage;
        const now = new Date();
        const msPassed = now.getTime() - baselineDate.getTime();
        const daysPassed = msPassed / (1000 * 60 * 60 * 24);
        
        const isInitial = task.last_performed_mileage === null && task.last_performed_date === null;
        const wasMileageEstimated = task.last_performed_mileage === null && task.last_performed_date !== null;
        
        let distProgress = 0;
        let timeProgress = 0;
        
        if (targetDistance && targetDistance > 0) {
            distProgress = drivenSinceLast / targetDistance;
            remainingDistance = targetDistance - drivenSinceLast;
            if (remainingDistance <= 0) isOverdue = true;
        }
        
        if (targetMonths && targetMonths > 0) {
            const targetDays = targetMonths * 30.44; // approx
            timeProgress = daysPassed / targetDays;
            remainingDays = Math.round(targetDays - daysPassed);
            if (remainingDays <= 0) isOverdue = true;
        }
        
        // A task is "Pending" if the user says history exists but hasn't provided it,
        // or if it's AI-generated and untouched.
        // It is NOT pending if history_confirmed is explicitly false (confirmed never done).
        const isPending = (task.history_confirmed !== false) && task.last_performed_mileage === null && task.last_performed_date === null;
        
        // Progress formatting
        const progress = Math.min(1.1, Math.max(distProgress, timeProgress)) * 100;
        
        // Final Status Type
        const statusType = isPending ? 'pending' : (isOverdue ? 'overdue' : 'ok');
        
        return { isOverdue, progress, remainingDistance, remainingDays, baselineMileage, baselineDate, targetDistance, targetMonths, currentMileage, isInitial: wasNeverPerformed, statusType };
    };

    const sortedTasks = useMemo(() => {
        if (!tasks) return [];
        return [...tasks].sort((a, b) => {
            if (sortMode === 'date') {
                return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
            }
            
            if (sortMode === 'type') {
                const typeOrder = a.action_type?.localeCompare(b.action_type || '') || 0;
                if (typeOrder !== 0) return typeOrder;
                return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
            }

            const statusA = getTaskStatus(a);
            const statusB = getTaskStatus(b);
            
            if (statusA.statusType === 'pending' && statusB.statusType !== 'pending') return 1;
            if (statusA.statusType !== 'pending' && statusB.statusType === 'pending') return -1;
            
            if (statusA.statusType === 'pending' && statusB.statusType === 'pending') {
                return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
            }
            
            if (statusA.statusType === 'overdue' && statusB.statusType !== 'overdue') return -1;
            if (statusA.statusType !== 'overdue' && statusB.statusType === 'overdue') return 1;
            
            return statusB.progress - statusA.progress;
        });
    }, [tasks, estimatedMileage, sortMode]);
    
    return (
        <PageTransition>
                <div className={`fixed inset-0 z-[60] flex flex-col ${theme.colors.pageBg}`}>
                    <NavigationBar
                        title={t.carTasks?.title || "Car Tasks"}
                        onBack={() => navigate(-1)}
                        fixed={false}
                        rightContent={
                            <div className="flex items-center gap-2 mr-1">
                                {isAdminPath && tasks && tasks.length > 0 && (
                                    <button 
                                        onClick={handleDeleteAll}
                                        disabled={isGenerating || isSubmitting}
                                        className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center hover:bg-red-200 transition-colors"
                                    >
                                        <Trash2 size={20} />
                                    </button>
                                )}
                                <button 
                                    onClick={() => navigate('new')}
                                    className="bg-blue-600 dark:bg-blue-500 rounded-full p-2 text-white hover:bg-blue-700 dark:hover:bg-blue-400 transition-colors shadow-sm flex items-center justify-center"
                                >
                                    <Plus size={22} strokeWidth={2.5} />
                                </button>
                            </div>
                        }
                    />

                <div className="flex-1 overflow-y-auto no-scrollbar pb-32">
                    <div className={`${theme.spacing.pagePadding} pt-4 space-y-6 relative max-w-2xl mx-auto`}>
                        
                        {canGenerate && (
                            <button
                                onClick={handleGenerate}
                                disabled={isGenerating}
                                className="w-full relative overflow-hidden p-5 rounded-[2rem] bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-xl shadow-purple-500/20 active:scale-[0.98] transition-all disabled:opacity-75 disabled:cursor-wait"
                            >
                                <div className="relative z-10 flex items-center justify-between">
                                    <div className="text-left">
                                        <h3 className="text-lg font-black flex items-center gap-2 tracking-tight">
                                            <Sparkles size={18} /> {isGenerating ? 'Ātri ģenerēju plānu...' : 'AI Apkopes Grafiks'}
                                        </h3>
                                        <p className="text-sm font-medium text-white/80">Uzģenerēt sarakstu pēc tava auto datiem</p>
                                    </div>
                                    <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center shrink-0 shadow-inner">
                                        {isGenerating ? <Loader2 size={24} className="animate-spin text-white" /> : <Brain size={24} className="text-white drop-shadow-md" />}
                                    </div>
                                </div>
                                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 blur-2xl" />
                            </button>
                        )}

                        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 -mx-4 px-4">
                            {[
                                { id: 'priority', label: t.carTasks.sortPriority },
                                { id: 'date', label: t.carTasks.sortDate },
                                { id: 'type', label: t.carTasks.sortType }
                            ].map(mode => (
                                <button 
                                    key={mode.id}
                                    onClick={() => setSortMode(mode.id as any)}
                                    className={`px-4 py-2.5 rounded-2xl text-[13px] font-black uppercase tracking-wider transition-all flex-shrink-0 border-2 ${
                                        sortMode === mode.id 
                                        ? 'bg-blue-600 border-blue-600 text-white shadow-lg shadow-blue-500/20' 
                                        : 'bg-white dark:bg-slate-900 border-gray-100 dark:border-white/5 text-slate-500 dark:text-slate-400'
                                    }`}
                                >
                                    {mode.label}
                                </button>
                            ))}
                        </div>

                        {isLoading && !isGenerating && (
                            <div className="flex flex-col items-center justify-center py-20 gap-4">
                                <Loader2 className="animate-spin text-blue-500" size={32} />
                                <p className="text-slate-400 font-bold animate-pulse uppercase tracking-widest text-[10px]">
                                    {t.common?.loading || 'Loading tasks...'}
                                </p>
                            </div>
                        )}

                        {(sortedTasks.length > 0 || (!isLoading && !isGenerating)) && (
                            <div key={car?.id} className={styles.card}>
                                <div className="divide-y divide-gray-100 dark:divide-white/5">
                                    {sortedTasks.map(task => {
                                        const status = getTaskStatus(task);
                                        
                                        let ActionIcon = Wrench;
                                        if (task.action_type === 'inspect') ActionIcon = Eye;
                                        if (task.action_type === 'service') ActionIcon = Droplet;

                                        return (
                                            <div 
                                                key={task.id} 
                                                onClick={() => navigate(task.id)}
                                                className={`p-4 relative transition-colors duration-500 cursor-pointer group ${status.isOverdue ? 'bg-red-50/50 dark:bg-red-900/10 hover:bg-red-50 dark:hover:bg-red-900/20' : 'hover:bg-gray-50 dark:hover:bg-white/5'}`}
                                            >
                                                
                                                <div className="flex justify-between items-start mb-2">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center shadow-sm ${status.isOverdue ? 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400' : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'}`}>
                                                            <ActionIcon size={20} strokeWidth={2.5} />
                                                        </div>
                                                        <div className="flex flex-col">
                                                            <h4 className="font-black text-gray-900 dark:text-white capitalize leading-tight">{task.name}</h4>
                                                            <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">
                                                                {task.action_type || 'replace'}{task.category ? ` • ${task.category}` : ''}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    {status.isOverdue && status.statusType !== 'pending' && (
                                                        <span className="flex items-center gap-1 text-[10px] font-black text-red-600 dark:text-red-400 uppercase tracking-widest bg-red-100 dark:bg-red-900/40 px-2 py-1 rounded-md animate-pulse">
                                                            <AlertCircle size={12} strokeWidth={3} /> {t.carTasks.status.overdue}
                                                        </span>
                                                    )}
                                                </div>
                                                
                                                {task.description && (
                                                    <p className="text-sm text-slate-600 dark:text-slate-400 my-2.5 font-medium leading-relaxed ml-1">{task.description}</p>
                                                )}

                                                <div className="mt-3 space-y-1.5 ml-1">
                                                    <div className="flex justify-between items-center text-[10px] font-black tracking-wide uppercase">
                                                        <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                                                            {status.statusType === 'pending' ? (
                                                                <span className="text-amber-600 dark:text-amber-400 flex items-center gap-1.5 font-bold tracking-tight">
                                                                    <AlertTriangle size={14} /> {t.carTasks.status.noData}
                                                                </span>
                                                            ) : (
                                                                <div className="flex items-center gap-1.5">
                                                                    {status.remainingDistance !== null && (
                                                                        <span className={status.statusType === 'overdue' ? 'text-red-500' : ''}>
                                                                            {status.remainingDistance > 0 ? t.carTasks.status.nextIn.replace('{dist}', status.remainingDistance.toLocaleString()).replace('{unit}', 'km') : t.carTasks.status.overdueBy.replace('{dist}', Math.abs(status.remainingDistance).toLocaleString()).replace('{unit}', 'km')}
                                                                        </span>
                                                                    )}
                                                                    
                                                                    {status.remainingDistance !== null && status.remainingDays !== null && (
                                                                        <span className="opacity-30">•</span>
                                                                    )}

                                                                    {status.remainingDays !== null && (
                                                                        <span className={`${status.statusType === 'overdue' ? 'text-red-500' : 'opacity-80'}`}>
                                                                            {status.remainingDays > 0 ? t.carTasks.status.afterDays.replace('{days}', String(status.remainingDays)) : t.carTasks.status.overdueDays.replace('{days}', String(Math.abs(status.remainingDays)))}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <span className={status.statusType === 'overdue' ? 'text-red-600' : (status.statusType === 'pending' ? 'text-amber-600' : 'text-slate-500 dark:text-slate-400')}>
                                                            {status.statusType === 'pending' ? '' : `${Math.round(status.progress)}%`}
                                                        </span>
                                                    </div>
                                                    {status.statusType !== 'pending' && (
                                                        <div className="h-1.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                                            <div 
                                                                className={`h-full rounded-full transition-all duration-1000 ${
                                                                    status.statusType === 'overdue' ? 'bg-red-500' : 
                                                                    (status.progress > 80 ? 'bg-orange-500' : 'bg-blue-500')
                                                                }`}
                                                                style={{ width: `${Math.min(100, Math.max(0, status.progress))}%` }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                    
                                    {sortedTasks.length === 0 && !isLoading && !isGenerating && (
                                        <div className="text-center py-12 px-6">
                                            <div className="w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-800 mx-auto flex items-center justify-center mb-4 text-slate-400">
                                                <Wrench size={32} />
                                            </div>
                                            <h3 className="text-lg font-black text-slate-900 dark:text-white mb-2">Nav aktīvu uzdevumu</h3>
                                            <p className="text-slate-500 font-medium">Uzģenerē AI apkopes plānu vai pievieno savus uzdevumus pats.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        
                    </div>
                </div>

                <AlertModal
                    isOpen={alertConfig.isOpen}
                    onClose={() => setAlertConfig(prev => ({ ...prev, isOpen: false }))}
                    title={alertConfig.title}
                    message={alertConfig.message}
                    confirmLabel={alertConfig.confirmLabel}
                    secondaryAction={alertConfig.secondaryAction}
                />
            </div>
        </PageTransition>
    );
}
